/**
 * ShopsPage — list user's shops with slim preview cards.
 * Click a card → /shops/:id for full editing.
 * Includes: profile editing, filter/sort, add new shop.
 */
import { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthStore } from '../../stores/authStore';
import { useShopStore } from '../../stores/shopStore';
import { apiUrl } from '../../config';
import { useTranslation } from 'react-i18next';
import './ShopsPage.css';

const COUNTRY_CODES = [
  { code: '+1', flag: '🇺🇸', name: 'US/Canada' },
  { code: '+7', flag: '🇷🇺', name: 'Russia' },
  { code: '+20', flag: '🇪🇬', name: 'Egypt' },
  { code: '+27', flag: '🇿🇦', name: 'South Africa' },
  { code: '+30', flag: '🇬🇷', name: 'Greece' },
  { code: '+31', flag: '🇳🇱', name: 'Netherlands' },
  { code: '+32', flag: '🇧🇪', name: 'Belgium' },
  { code: '+33', flag: '🇫🇷', name: 'France' },
  { code: '+34', flag: '🇪🇸', name: 'Spain' },
  { code: '+36', flag: '🇭🇺', name: 'Hungary' },
  { code: '+39', flag: '🇮🇹', name: 'Italy' },
  { code: '+40', flag: '🇷🇴', name: 'Romania' },
  { code: '+41', flag: '🇨🇭', name: 'Switzerland' },
  { code: '+43', flag: '🇦🇹', name: 'Austria' },
  { code: '+44', flag: '🇬🇧', name: 'UK' },
  { code: '+45', flag: '🇩🇰', name: 'Denmark' },
  { code: '+46', flag: '🇸🇪', name: 'Sweden' },
  { code: '+47', flag: '🇳🇴', name: 'Norway' },
  { code: '+48', flag: '🇵🇱', name: 'Poland' },
  { code: '+49', flag: '🇩🇪', name: 'Germany' },
  { code: '+51', flag: '🇵🇪', name: 'Peru' },
  { code: '+52', flag: '🇲🇽', name: 'Mexico' },
  { code: '+53', flag: '🇨🇺', name: 'Cuba' },
  { code: '+54', flag: '🇦🇷', name: 'Argentina' },
  { code: '+55', flag: '🇧🇷', name: 'Brazil' },
  { code: '+56', flag: '🇨🇱', name: 'Chile' },
  { code: '+57', flag: '🇨🇴', name: 'Colombia' },
  { code: '+58', flag: '🇻🇪', name: 'Venezuela' },
  { code: '+60', flag: '🇲🇾', name: 'Malaysia' },
  { code: '+61', flag: '🇦🇺', name: 'Australia' },
  { code: '+62', flag: '🇮🇩', name: 'Indonesia' },
  { code: '+63', flag: '🇵🇭', name: 'Philippines' },
  { code: '+64', flag: '🇳🇿', name: 'New Zealand' },
  { code: '+65', flag: '🇸🇬', name: 'Singapore' },
  { code: '+66', flag: '🇹🇭', name: 'Thailand' },
  { code: '+81', flag: '🇯🇵', name: 'Japan' },
  { code: '+82', flag: '🇰🇷', name: 'South Korea' },
  { code: '+84', flag: '🇻🇳', name: 'Vietnam' },
  { code: '+86', flag: '🇨🇳', name: 'China' },
  { code: '+90', flag: '🇹🇷', name: 'Turkey' },
  { code: '+91', flag: '🇮🇳', name: 'India' },
  { code: '+92', flag: '🇵🇰', name: 'Pakistan' },
  { code: '+93', flag: '🇦🇫', name: 'Afghanistan' },
  { code: '+94', flag: '🇱🇰', name: 'Sri Lanka' },
  { code: '+95', flag: '🇲🇲', name: 'Myanmar' },
  { code: '+98', flag: '🇮🇷', name: 'Iran' },
  { code: '+211', flag: '🇸🇸', name: 'South Sudan' },
  { code: '+212', flag: '🇲🇦', name: 'Morocco' },
  { code: '+213', flag: '🇩🇿', name: 'Algeria' },
  { code: '+216', flag: '🇹🇳', name: 'Tunisia' },
  { code: '+218', flag: '🇱🇾', name: 'Libya' },
  { code: '+220', flag: '🇬🇲', name: 'Gambia' },
  { code: '+221', flag: '🇸🇳', name: 'Senegal' },
  { code: '+233', flag: '🇬🇭', name: 'Ghana' },
  { code: '+234', flag: '🇳🇬', name: 'Nigeria' },
  { code: '+249', flag: '🇸🇩', name: 'Sudan' },
  { code: '+251', flag: '🇪🇹', name: 'Ethiopia' },
  { code: '+252', flag: '🇸🇴', name: 'Somalia' },
  { code: '+254', flag: '🇰🇪', name: 'Kenya' },
  { code: '+255', flag: '🇹🇿', name: 'Tanzania' },
  { code: '+256', flag: '🇺🇬', name: 'Uganda' },
  { code: '+260', flag: '🇿🇲', name: 'Zambia' },
  { code: '+263', flag: '🇿🇼', name: 'Zimbabwe' },
  { code: '+351', flag: '🇵🇹', name: 'Portugal' },
  { code: '+353', flag: '🇮🇪', name: 'Ireland' },
  { code: '+354', flag: '🇮🇸', name: 'Iceland' },
  { code: '+355', flag: '🇦🇱', name: 'Albania' },
  { code: '+358', flag: '🇫🇮', name: 'Finland' },
  { code: '+370', flag: '🇱🇹', name: 'Lithuania' },
  { code: '+371', flag: '🇱🇻', name: 'Latvia' },
  { code: '+372', flag: '🇪🇪', name: 'Estonia' },
  { code: '+380', flag: '🇺🇦', name: 'Ukraine' },
  { code: '+381', flag: '🇷🇸', name: 'Serbia' },
  { code: '+385', flag: '🇭🇷', name: 'Croatia' },
  { code: '+386', flag: '🇸🇮', name: 'Slovenia' },
  { code: '+387', flag: '🇧🇦', name: 'Bosnia' },
  { code: '+389', flag: '🇲🇰', name: 'N. Macedonia' },
  { code: '+420', flag: '🇨🇿', name: 'Czech Republic' },
  { code: '+421', flag: '🇸🇰', name: 'Slovakia' },
  { code: '+852', flag: '🇭🇰', name: 'Hong Kong' },
  { code: '+880', flag: '🇧🇩', name: 'Bangladesh' },
  { code: '+886', flag: '🇹🇼', name: 'Taiwan' },
  { code: '+960', flag: '🇲🇻', name: 'Maldives' },
  { code: '+961', flag: '🇱🇧', name: 'Lebanon' },
  { code: '+962', flag: '🇯🇴', name: 'Jordan' },
  { code: '+963', flag: '🇸🇾', name: 'Syria' },
  { code: '+964', flag: '🇮🇶', name: 'Iraq' },
  { code: '+965', flag: '🇰🇼', name: 'Kuwait' },
  { code: '+966', flag: '🇸🇦', name: 'Saudi Arabia' },
  { code: '+967', flag: '🇾🇪', name: 'Yemen' },
  { code: '+968', flag: '🇴🇲', name: 'Oman' },
  { code: '+970', flag: '🇵🇸', name: 'Palestine' },
  { code: '+971', flag: '🇦🇪', name: 'UAE' },
  { code: '+972', flag: '🇮🇱', name: 'Israel' },
  { code: '+973', flag: '🇧🇭', name: 'Bahrain' },
  { code: '+974', flag: '🇶🇦', name: 'Qatar' },
  { code: '+975', flag: '🇧🇹', name: 'Bhutan' },
  { code: '+976', flag: '🇲🇳', name: 'Mongolia' },
  { code: '+977', flag: '🇳🇵', name: 'Nepal' },
  { code: '+992', flag: '🇹🇯', name: 'Tajikistan' },
  { code: '+993', flag: '🇹🇲', name: 'Turkmenistan' },
  { code: '+994', flag: '🇦🇿', name: 'Azerbaijan' },
  { code: '+995', flag: '🇬🇪', name: 'Georgia' },
  { code: '+996', flag: '🇰🇬', name: 'Kyrgyzstan' },
  { code: '+998', flag: '🇺🇿', name: 'Uzbekistan' },
];

/** Extract country code from a stored number like "+971501234567" */
function parseStoredNumber(full: string): { code: string; local: string } {
  if (!full) return { code: '+971', local: '' };
  // Try longest codes first to avoid partial matches (e.g. +1 matching +1xxx)
  const sorted = [...COUNTRY_CODES].sort((a, b) => b.code.length - a.code.length);
  for (const cc of sorted) {
    if (full.startsWith(cc.code)) {
      return { code: cc.code, local: full.slice(cc.code.length) };
    }
  }
  return { code: '+971', local: full.replace(/^\+/, '') };
}

export default function ShopsPage() {
  const { t } = useTranslation('shops');
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const updateProfile = useAuthStore((s) => s.updateProfile);
  const token = useAuthStore((s) => s.token);
  const navigate = useNavigate();
  const { shops, loading, fetchShops, createShop } = useShopStore();

  // Track which poster images failed to load (show fallback icon)
  const [posterErrors, setPosterErrors] = useState<Set<string>>(new Set());
  const handlePosterError = useCallback((shopId: string) => {
    setPosterErrors((prev) => new Set(prev).add(shopId));
  }, []);

  const [showAddShop, setShowAddShop] = useState(false);
  const [newShopName, setNewShopName] = useState('');
  const [newShopAddress, setNewShopAddress] = useState('');
  const [newShopLocation, setNewShopLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [showProfile, setShowProfile] = useState(false);
  const parsedPhone = parseStoredNumber(user?.phone || '');
  const parsedWhatsapp = parseStoredNumber(user?.whatsapp || '');
  const [phoneCode, setPhoneCode] = useState(parsedPhone.code);
  const [phone, setPhone] = useState(parsedPhone.local);
  const [waCode, setWaCode] = useState(parsedWhatsapp.code);
  const [whatsapp, setWhatsapp] = useState(parsedWhatsapp.local);
  const [sameAsPhone, setSameAsPhone] = useState(true);

  // Filter & sort state
  const [searchQuery, setSearchQuery] = useState('');
  const [visibilityFilter, setVisibilityFilter] = useState<'all' | 'public' | 'private'>('all');
  const [sortBy, setSortBy] = useState<'newest' | 'oldest' | 'name-az' | 'name-za' | 'sections'>('newest');

  // Filtered & sorted shops
  const filteredShops = useMemo(() => {
    let result = [...shops];

    // Search filter
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(s =>
        s.name.toLowerCase().includes(q) ||
        (s.address && s.address.toLowerCase().includes(q)) ||
        (s.category && s.category.toLowerCase().includes(q))
      );
    }

    // Visibility filter
    if (visibilityFilter !== 'all') {
      result = result.filter(s => s.visibility === visibilityFilter);
    }

    // Sort
    result.sort((a, b) => {
      switch (sortBy) {
        case 'newest':
          return (b.createdAt || '').localeCompare(a.createdAt || '');
        case 'oldest':
          return (a.createdAt || '').localeCompare(b.createdAt || '');
        case 'name-az':
          return a.name.localeCompare(b.name);
        case 'name-za':
          return b.name.localeCompare(a.name);
        case 'sections':
          return (b.sections?.length || 0) - (a.sections?.length || 0);
        default:
          return 0;
      }
    });

    return result;
  }, [shops, searchQuery, visibilityFilter, sortBy]);

  // Read picked location from URL params (returned from LocationPickerPage)
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    fetchShops();
  }, []);

  // Handle location picked from LocationPickerPage (for new shop only)
  useEffect(() => {
    const pickedLat = searchParams.get('pickedLat');
    const pickedLng = searchParams.get('pickedLng');
    const forNewShop = searchParams.get('forNewShop');

    if (pickedLat && pickedLng && forNewShop === 'true') {
      const lat = parseFloat(pickedLat);
      const lng = parseFloat(pickedLng);
      if (!isNaN(lat) && !isNaN(lng)) {
        setNewShopLocation({ lat, lng });
        setShowAddShop(true);
      }
      searchParams.delete('pickedLat');
      searchParams.delete('pickedLng');
      searchParams.delete('forNewShop');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams]);

  async function handleCreateShop(e: React.FormEvent) {
    e.preventDefault();
    if (!newShopName.trim()) return;
    const shop = await createShop(newShopName.trim(), newShopAddress.trim(), '', newShopLocation);
    if (shop) {
      setNewShopName('');
      setNewShopAddress('');
      setNewShopLocation(null);
      setShowAddShop(false);
    } else {
      // Show store error (e.g. shop limit reached) as alert
      const err = useShopStore.getState().error;
      if (err) alert(err);
    }
  }

  function handleGetCurrentLocation() {
    // Navigate to the map picker; pass forNewShop flag
    const params = new URLSearchParams({ returnTo: '/shops', forNewShop: 'true' });
    if (newShopLocation) {
      params.set('lat', newShopLocation.lat.toString());
      params.set('lng', newShopLocation.lng.toString());
    }
    navigate(`/location-picker?${params.toString()}`);
  }

  async function handleSaveProfile(e: React.FormEvent) {
    e.preventDefault();
    const fullPhone = phone ? `${phoneCode}${phone}` : '';
    const fullWhatsapp = sameAsPhone ? fullPhone : (whatsapp ? `${waCode}${whatsapp}` : '');
    await updateProfile(fullPhone, fullWhatsapp);
    setShowProfile(false);
  }

  return (
    <div className="shops-page">
      {/* Header */}
      <header className="shops-header">
        <div className="shops-header-left">
          <span className="shops-logo">🏪</span>
          <h1>{t('myShops')}</h1>
        </div>
        <div className="shops-header-right">
          <button className="shops-btn-map" onClick={() => navigate('/shops/map')} title={t('viewMap')}>
            🗺️
          </button>
          {user?.picture ? (
            <img
              src={user.picture}
              className="shops-avatar"
              alt=""
              referrerPolicy="no-referrer"
              onClick={() => setShowProfile(!showProfile)}
            />
          ) : (
            <span className="shops-avatar-fallback" onClick={() => setShowProfile(!showProfile)}>
              {user?.name?.charAt(0) || '?'}
            </span>
          )}
          <button className="shops-btn-text" onClick={() => { logout(); navigate('/login', { replace: true }); }}>{t('signOut')}</button>
        </div>
      </header>

      {/* WhatsApp prompt — shows when phone is empty */}
      {user && !user.phone && !showProfile && (
        <form className="whatsapp-prompt" onSubmit={handleSaveProfile}>
          <div className="whatsapp-prompt-header">
            <svg className="whatsapp-icon" viewBox="0 0 24 24" fill="#25D366" width="28" height="28">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347zM12.05 21.785h-.01a9.865 9.865 0 01-5.031-1.378l-.361-.214-3.741.981.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884zM20.52 3.449C18.24 1.245 15.24 0 12.045 0 5.463 0 .104 5.334.101 11.893c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.585 0 11.946-5.336 11.949-11.896 0-3.176-1.24-6.165-3.495-8.411"/>
            </svg>
            <div className="whatsapp-prompt-text">
              <strong>{t('completeYourProfile')}</strong>
              <span>{t('addWhatsAppPrompt')}</span>
            </div>
          </div>
          <div className="phone-row">
            <select value={phoneCode} onChange={(e) => setPhoneCode(e.target.value)} className="country-code-select">
              {COUNTRY_CODES.map((c) => (
                <option key={c.code} value={c.code}>{c.flag} {c.code}</option>
              ))}
            </select>
            <input
              type="tel"
              placeholder={t('phoneNumber')}
              value={phone}
              onChange={(e) => setPhone(e.target.value.replace(/[^0-9]/g, ''))}
              className="shops-input shops-input-sm phone-input"
            />
          </div>
          <label className="same-number-check">
            <input type="checkbox" checked={sameAsPhone} onChange={(e) => setSameAsPhone(e.target.checked)} />
            <span>{t('whatsAppSameAsPhone')}</span>
          </label>
          {!sameAsPhone && (
            <div className="phone-row">
              <select value={waCode} onChange={(e) => setWaCode(e.target.value)} className="country-code-select">
                {COUNTRY_CODES.map((c) => (
                  <option key={c.code} value={c.code}>{c.flag} {c.code}</option>
                ))}
              </select>
              <input
                type="tel"
                placeholder={t('whatsAppNumber')}
                value={whatsapp}
                onChange={(e) => setWhatsapp(e.target.value.replace(/[^0-9]/g, ''))}
                className="shops-input shops-input-sm phone-input"
              />
            </div>
          )}
          <button type="submit" className="whatsapp-prompt-btn">{t('common:save')}</button>
        </form>
      )}

      {/* Profile Panel (avatar tap — for editing after initial setup) */}
      {showProfile && (
        <form className="profile-panel" onSubmit={handleSaveProfile}>
          <div className="profile-info">
            <strong>{user?.name}</strong>
            <span className="profile-email">{user?.email}</span>
          </div>
          <div className="phone-row">
            <select value={phoneCode} onChange={(e) => setPhoneCode(e.target.value)} className="country-code-select">
              {COUNTRY_CODES.map((c) => (
                <option key={c.code} value={c.code}>{c.flag} {c.code}</option>
              ))}
            </select>
            <input
              type="tel"
              placeholder={t('phoneNumber')}
              value={phone}
              onChange={(e) => setPhone(e.target.value.replace(/[^0-9]/g, ''))}
              className="shops-input shops-input-sm phone-input"
            />
          </div>
          <label className="same-number-check">
            <input type="checkbox" checked={sameAsPhone} onChange={(e) => setSameAsPhone(e.target.checked)} />
            <span>{t('whatsAppSameAsPhone')}</span>
          </label>
          {!sameAsPhone && (
            <div className="phone-row">
              <select value={waCode} onChange={(e) => setWaCode(e.target.value)} className="country-code-select">
                {COUNTRY_CODES.map((c) => (
                  <option key={c.code} value={c.code}>{c.flag} {c.code}</option>
                ))}
              </select>
              <input
                type="tel"
                placeholder={t('whatsAppNumber')}
                value={whatsapp}
                onChange={(e) => setWhatsapp(e.target.value.replace(/[^0-9]/g, ''))}
                className="shops-input shops-input-sm phone-input"
              />
            </div>
          )}
          <div className="shops-add-actions">
            <button type="submit" className="shops-btn-primary shops-btn-sm">{t('common:save')}</button>
            <button type="button" className="shops-btn-text" onClick={() => setShowProfile(false)}>{t('common:close')}</button>
          </div>
        </form>
      )}

      {/* Filter & Sort Bar */}
      {shops.length > 0 && (
        <div className="shops-filter-bar">
          <div className="shops-search-row">
            <input
              type="text"
              placeholder={t('searchShops')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="shops-search-input"
            />
            {searchQuery && (
              <button className="shops-search-clear" onClick={() => setSearchQuery('')}>✕</button>
            )}
          </div>
          <div className="shops-filter-controls">
            <div className="shops-visibility-chips">
              {(['all', 'public', 'private'] as const).map((v) => (
                <button
                  key={v}
                  className={`shops-chip ${visibilityFilter === v ? 'shops-chip--active' : ''}`}
                  onClick={() => setVisibilityFilter(v)}
                >
                  {v === 'all' ? t('filterAll') : v === 'public' ? t('filterPublic') : t('filterPrivate')}
                </button>
              ))}
            </div>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as any)}
              className="shops-sort-select"
            >
              <option value="newest">{t('sortNewest')}</option>
              <option value="oldest">{t('sortOldest')}</option>
              <option value="name-az">{t('sortNameAZ')}</option>
              <option value="name-za">{t('sortNameZA')}</option>
              <option value="sections">{t('sortMostSections')}</option>
            </select>
          </div>
          {(searchQuery || visibilityFilter !== 'all') && (
            <div className="shops-filter-status">
              {t('filterStatus', { shown: filteredShops.length, total: shops.length })}
            </div>
          )}
        </div>
      )}

      {/* Add Shop */}
      {!showAddShop ? (
        <button className="shops-btn-add" onClick={() => setShowAddShop(true)}>
          + {t('addNewShop')}
        </button>
      ) : (
        <form className="shops-add-form" onSubmit={handleCreateShop}>
          <input
            type="text"
            placeholder={t('shopName')}
            value={newShopName}
            onChange={(e) => setNewShopName(e.target.value)}
            autoFocus
            className="shops-input"
          />
          <input
            type="text"
            placeholder={t('addressOptional')}
            value={newShopAddress}
            onChange={(e) => setNewShopAddress(e.target.value)}
            className="shops-input"
          />
          
          {/* Location capture */}
          <div className="shops-location-capture">
            {!newShopLocation ? (
              <button 
                type="button" 
                className="shops-btn-location" 
                onClick={handleGetCurrentLocation}
              >
                {t('getCurrentLocation')}
              </button>
            ) : (
              <div className="shops-location-display">
                <span className="shops-location-coords">
                  📍 {newShopLocation.lat.toFixed(6)}, {newShopLocation.lng.toFixed(6)}
                </span>
                <button 
                  type="button" 
                  className="shops-btn-location-clear" 
                  onClick={() => setNewShopLocation(null)}
                >
                  {t('common:clear')}
                </button>
              </div>
            )}
          </div>

          <div className="shops-add-actions">
            <button type="submit" className="shops-btn-primary">{t('create')}</button>
            <button type="button" className="shops-btn-text" onClick={() => { setShowAddShop(false); setNewShopLocation(null); }}>{t('common:cancel')}</button>
          </div>
        </form>
      )}

      {/* Shop List */}
      {loading && shops.length === 0 ? (
        <div className="shops-empty">{t('common:loadingDots')}</div>
      ) : shops.length === 0 ? (
        <div className="shops-empty">
          <p>{t('noShopsYet')}</p>
          <p className="shops-empty-sub">{t('createFirstShop')}</p>
        </div>
      ) : filteredShops.length === 0 ? (
        <div className="shops-empty">
          <p>{t('noShopsMatchFilters')}</p>
          <button className="shops-btn-text" onClick={() => { setSearchQuery(''); setVisibilityFilter('all'); }}>{t('clearFilters')}</button>
        </div>
      ) : (
        <div className="shops-list">
          {filteredShops.map((shop) => (
            <div
              key={shop.id}
              className="shop-list-card"
              onClick={() => navigate(`/shops/${shop.id}`)}
            >
              <div className="shop-list-card-poster">
                {posterErrors.has(shop.id) ? (
                  <div className="shop-list-card-poster-fallback">🏪</div>
                ) : (
                  <img
                    src={apiUrl(`/api/shops/${shop.id}/poster${token ? `?token=${token}` : ''}${shop.posterSectionId ? `${token ? '&' : '?'}_s=${shop.posterSectionId}` : ''}`)}
                    alt=""
                    loading="lazy"
                    className="shop-list-card-poster-img"
                    onError={() => handlePosterError(shop.id)}
                  />
                )}
              </div>
              <div className="shop-list-card-info">
                <h3 className="shop-list-card-name">
                  {shop.name}
                  {shop.visibility === 'public' && (
                    <span className="visibility-badge visibility-public">{t('statusPublic')}</span>
                  )}
                  {shop.visibility === 'private' && (
                    <span className="visibility-badge visibility-private">{t('statusPrivate')}</span>
                  )}
                </h3>
                {shop.address && <p className="shop-list-card-address">{shop.address}</p>}
                <p className="shop-list-card-meta">
                  {t('sectionCount', { count: shop.sections.length })}
                  {shop.location && ` · ${t('locationSet')}`}
                </p>
              </div>
              <div className="shop-list-card-actions">
                <button
                  className="shop-list-card-dash-btn"
                  title={t('ordersDashboard')}
                  onClick={(e) => { e.stopPropagation(); navigate(`/dashboard/${shop.id}`); }}
                >📊</button>
                <span className="shop-list-card-arrow">›</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
